import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Percent, RefreshCw } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';

const AdminDiscountTiers = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    min_quantity: '',
    max_quantity: '',
    discount_percentage: '',
    label: '',
    sort_order: 0,
    is_active: true
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (adminToken) loadTiers();
  }, [adminToken]); // eslint-disable-line

  const loadTiers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/admin/discount-tiers`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      const data = await response.json();
      setTiers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading discount tiers:', error);
      setTiers([]);
      toast({
        title: "Errore",
        description: "Impossibile caricare le fasce di sconto",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Parse valori
    const minQ = parseInt(formData.min_quantity);
    const maxRaw = formData.max_quantity;
    const maxQ = (maxRaw === '' || maxRaw === null || maxRaw === undefined) ? null : parseInt(maxRaw);
    
    // Validazione: se max è definito deve essere > min
    if (maxQ !== null && !isNaN(maxQ) && minQ >= maxQ) {
      toast({
        title: "Errore di Validazione",
        description: "La quantità minima deve essere inferiore alla quantità massima",
        variant: "destructive"
      });
      return;
    }

    if (parseFloat(formData.discount_percentage) < 0 || parseFloat(formData.discount_percentage) > 100) {
      toast({
        title: "Errore di Validazione",
        description: "Lo sconto deve essere tra 0 e 100%",
        variant: "destructive"
      });
      return;
    }

    try {
      const url = editingTier 
        ? `${backendUrl}/api/admin/discount-tiers/${editingTier.id}`
        : `${backendUrl}/api/admin/discount-tiers`;
      
      const method = editingTier ? 'PUT' : 'POST';
      
      // Converte la percentuale da formato umano (10) a formato decimale (0.10)
      const submitData = {
        ...formData,
        min_quantity: minQ,
        max_quantity: maxQ,
        discount_percentage: parseFloat(formData.discount_percentage) / 100,
        sort_order: parseInt(formData.sort_order)
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast({
          title: "Successo",
          description: editingTier ? "Fascia di sconto aggiornata" : "Fascia di sconto creata"
        });
        loadTiers();
        resetForm();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save tier');
      }
    } catch (error) {
      console.error('Error saving tier:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile salvare la fascia di sconto",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (tier) => {
    setEditingTier(tier);
    setFormData({
      min_quantity: tier.min_quantity,
      max_quantity: tier.max_quantity,
      discount_percentage: (tier.discount_percentage * 100).toFixed(0), // Converte 0.10 a 10
      label: tier.label,
      sort_order: tier.sort_order,
      is_active: tier.is_active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (tierId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa fascia di sconto?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/discount-tiers/${tierId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        toast({
          title: "Successo",
          description: "Fascia di sconto eliminata"
        });
        loadTiers();
      } else {
        throw new Error('Failed to delete tier');
      }
    } catch (error) {
      console.error('Error deleting tier:', error);
      toast({
        title: "Errore",
        description: "Impossibile eliminare la fascia di sconto",
        variant: "destructive"
      });
    }
  };

  const handleInitialize = async () => {
    if (!window.confirm('Questa azione inizializzerà le fasce di sconto con i valori di default. Procedi solo se non esistono già fasce configurate.')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/admin/discount-tiers/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Successo",
          description: data.message
        });
        loadTiers();
      } else {
        toast({
          title: "Informazione",
          description: data.message,
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error initializing tiers:', error);
      toast({
        title: "Errore",
        description: "Impossibile inizializzare le fasce di sconto",
        variant: "destructive"
      });
    }
  };

  const resetForm = () => {
    setFormData({
      min_quantity: '',
      max_quantity: '',
      discount_percentage: '',
      label: '',
      sort_order: 0,
      is_active: true
    });
    setEditingTier(null);
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Caricamento...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Percent className="w-8 h-8 text-purple-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestione Sconti Quantità</h1>
              <p className="text-gray-600 mt-1">Configura le fasce di sconto per il listino prezzi</p>
            </div>
          </div>
          <div className="flex space-x-2">
            {tiers.length === 0 && (
              <button
                onClick={handleInitialize}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Inizializza Default
              </button>
            )}
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuova Fascia
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
          <p className="text-sm text-blue-700">
            <strong>Nota:</strong> Le fasce di sconto configurate qui vengono applicate automaticamente nella pagina <strong>/listino</strong>. 
            Gli sconti sono espressi in percentuale (es: 10% = sconto del 10%).
          </p>
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingTier ? 'Modifica Fascia' : 'Nuova Fascia'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantità Minima *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.min_quantity}
                    onChange={(e) => setFormData({...formData, min_quantity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="es: 24"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantità Massima
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.max_quantity ?? ''}
                    onChange={(e) => setFormData({...formData, max_quantity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="es: 48 (lascia vuoto = illimitato)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Lascia vuoto per una fascia senza limite superiore (es: &ldquo;+400 bot.&rdquo;)</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sconto Percentuale * (%)
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({...formData, discount_percentage: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="es: 10"
                  />
                  <p className="text-xs text-gray-500 mt-1">Inserisci il valore in % (es: 10 per 10%)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Etichetta *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.label}
                    onChange={(e) => setFormData({...formData, label: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    placeholder="es: 24-48 bot."
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ordine di Visualizzazione
                  </label>
                  <input
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div className="flex items-center">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Fascia Attiva</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex items-center px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingTier ? 'Aggiorna' : 'Crea'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Annulla
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tiers List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ordine
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Etichetta
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Range Quantità
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sconto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tiers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      <Percent className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>Nessuna fascia di sconto configurata</p>
                      <p className="text-sm mt-1">Clicca su "Inizializza Default" per creare le fasce standard</p>
                    </td>
                  </tr>
                ) : (
                  tiers.map((tier) => (
                    <tr key={tier.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tier.sort_order}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{tier.label}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {tier.min_quantity} - {tier.max_quantity} bottiglie
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-green-600">
                          {(tier.discount_percentage * 100).toFixed(0)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          tier.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {tier.is_active ? 'Attivo' : 'Disattivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(tier)}
                          className="text-purple-600 hover:text-purple-900 mr-3"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(tier.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary */}
        {tiers.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Riepilogo</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Totale Fasce:</span>
                <span className="ml-2 font-semibold text-gray-900">{tiers.length}</span>
              </div>
              <div>
                <span className="text-gray-600">Fasce Attive:</span>
                <span className="ml-2 font-semibold text-green-600">
                  {tiers.filter(t => t.is_active).length}
                </span>
              </div>
              <div>
                <span className="text-gray-600">Range Totale:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {Math.min(...tiers.map(t => t.min_quantity))} - {Math.max(...tiers.map(t => t.max_quantity))} bottiglie
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDiscountTiers;
